import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type {
  AcceptStaffInviteRequest,
  AcceptedStaffInviteDTO,
  CreateStaffInviteRequest,
  StaffInviteDTO,
  StaffMemberDTO,
} from "@membentry/shared-types";
import {
  acceptStaffInvite,
  createStaffInvite,
  disableStaff,
  enableStaff,
  getStaff,
  getStaffInvites,
  revokeStaffInvite,
} from "@membentry/shared-api";

export const staffQueryKey = (clubId: number) =>
  ["clubs", clubId, "staff"] as const;
export const staffInvitesQueryKey = (clubId: number) =>
  [...staffQueryKey(clubId), "invites"] as const;

export function useStaffQuery(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseQueryOptions<StaffMemberDTO[], Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: staffQueryKey(clubId),
    queryFn: () => getStaff(client, clubId),
    enabled: !!clubId,
    ...options,
  });
}
export function useStaffInvitesQuery(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseQueryOptions<StaffInviteDTO[], Error>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: staffInvitesQueryKey(clubId),
    queryFn: () => getStaffInvites(client, clubId),
    enabled: !!clubId,
    ...options,
  });
}
function useStaffMutation<TData, TVariables>(
  client: AxiosInstance,
  clubId: number,
  mutationFn: (value: TVariables) => Promise<TData>,
  options?: Omit<
    UseMutationOptions<TData, Error, TVariables>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const onSuccess = options?.onSuccess;
  return useMutation({
    ...options,
    mutationFn,
    onSuccess: async (data, variables, onMutateResult, context) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: staffQueryKey(clubId) }),
        queryClient.invalidateQueries({
          queryKey: staffInvitesQueryKey(clubId),
        }),
      ]);
      await onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
export const useCreateStaffInviteMutation = (
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<StaffInviteDTO, Error, CreateStaffInviteRequest>,
    "mutationFn"
  >,
) =>
  useStaffMutation(
    client,
    clubId,
    (request: CreateStaffInviteRequest) =>
      createStaffInvite(client, clubId, request),
    options,
  );
export const useRevokeStaffInviteMutation = (
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<StaffInviteDTO, Error, string>,
    "mutationFn"
  >,
) =>
  useStaffMutation(
    client,
    clubId,
    (id: string) => revokeStaffInvite(client, clubId, id),
    options,
  );
export const useDisableStaffMutation = (
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<StaffMemberDTO, Error, string>,
    "mutationFn"
  >,
) =>
  useStaffMutation(
    client,
    clubId,
    (id: string) => disableStaff(client, clubId, id),
    options,
  );
export const useEnableStaffMutation = (
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<StaffMemberDTO, Error, string>,
    "mutationFn"
  >,
) =>
  useStaffMutation(
    client,
    clubId,
    (id: string) => enableStaff(client, clubId, id),
    options,
  );

export function useAcceptStaffInviteMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      AcceptedStaffInviteDTO,
      Error,
      { inviteId: string } & AcceptStaffInviteRequest
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const onSuccess = options?.onSuccess;
  return useMutation({
    ...options,
    mutationFn: ({ inviteId, token }) =>
      acceptStaffInvite(client, inviteId, { token }),
    onSuccess: async (result, variables, onMutateResult, context) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: staffQueryKey(result.clubId),
        }),
        queryClient.invalidateQueries({
          queryKey: staffInvitesQueryKey(result.clubId),
        }),
      ]);
      await onSuccess?.(result, variables, onMutateResult, context);
    },
  });
}
